<template>
  <div>
    <!-- Hero Section -->
  <div class="bg-gradient-to-r from-green-500 to-yellow-300 rounded-xl shadow-lg p-8 text-white mb-8 flex flex-col md:flex-row items-center gap-8">
  <!-- Left Side (Text) -->
  <div class="flex-1">
    <h1 class="text-4xl font-bold mb-4">Welcome to MinSU Research Portal</h1>
    <p class="text-xl mb-4">Multidisciplinary Research Journal</p>

    <h2 class="text-2xl font-semibold mb-2">About the Journal</h2>
    <p class="mb-6">
      Daluyang Dunong (/daˈlujaŋ ˈdunoŋ/, "Channel of Knowledge") is the internationally peer-reviewed,
      open-access, multidisciplinary journal of Mindoro State University (MinSU). It serves as a platform 
      for publishing original research across a diverse range of academic fields.
    </p>

    <div class="flex flex-wrap gap-4">
      <button 
        @click="$emit('update-view', 'browse')" 
        class="px-6 py-3 bg-white text-green-600 rounded-lg font-medium hover:bg-gray-100 transition duration-200">
        Browse Journals
      </button>
      <button 
        @click="$emit('update-view', 'submit')" 
        class="px-6 py-3 border-2 border-white text-white rounded-lg font-medium hover:bg-white hover:bg-opacity-10 transition duration-200">
        Submit Your Research
      </button>
    </div>
  </div>

  <!-- Right Side (Image) -->
  <div class="flex-1 flex justify-center">
    <img src="@/assets/mrj_new.png" alt="Research Portal Logo" class="max-w-xs md:max-w-sm lg:max-w-md rounded-lg " />
  </div>
</div>

    <!-- Stats Section -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="bg-white p-6 rounded-xl shadow-md">
        <div class="flex items-center mb-4">
          <div class="p-3 rounded-full bg-green-100 text-green-600 mr-4">
            <i class="fas fa-book text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Latest Publications</h3>
            <p class="text-gray-600">{{ articles?.length || 0 }} article{s}</p>
          </div>
        </div>
        <p class="text-gray-700">Explore our collection of peer-reviewed research articles across various disciplines.</p>
      </div>

      <div class="bg-white p-6 rounded-xl shadow-md">
        <div class="flex items-center mb-4">
          <div class="p-3 rounded-full bg-green-100 text-green-600 mr-4">
            <i class="fas fa-users text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Research Community</h3>
            <p class="text-gray-600">Join {{ researchersCount }}  researchers</p>
          </div>
        </div>
        <p class="text-gray-700">Connect with researchers worldwide and collaborate on innovative projects.</p>
      </div>

      <div class="bg-white p-6 rounded-xl shadow-md">
        <div class="flex items-center mb-4">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
            <i class="fas fa-chart-line text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Impact Metrics</h3>
            <p class="text-gray-600">High citation rates</p>
          </div>
        </div>
        <p class="text-gray-700">Track the impact of your research with our comprehensive analytics dashboard.</p>
      </div>
    </div>

    <!-- Featured Journals -->
    <div v-if="featuredJournals && featuredJournals.length > 0" class="bg-white rounded-xl shadow-md overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-2xl font-bold text-gray-800">Featured Papers</h2>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        <div v-for="journal in featuredJournals" :key="journal.id" class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition duration-200">
          <div class="h-48 bg-gray-100 flex items-center justify-center">
            <i class="fas fa-book text-6xl text-gray-400"></i>
          </div>
          <div class="p-4">
            <router-link
                :to="`/article/${journal.slug}`"
                class="text-sm text-green-600 hover:text-green-800 font-medium"
              ><h3 class="text-lg font-semibold text-gray-800 mb-1">{{ journal.title }}</h3> </router-link>
              <p class="text-gray-600 text-sm mb-2">{{ journal.authors.join(', ') }}</p>
              <p class="text-gray-700 mb-3">{{ journal.abstract.substring(0, 50) }}...</p>
              <div class="flex flex-wrap gap-2">
                <span v-for="tag in journal.tags" :key="tag" class="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">{{ tag }}</span>
              </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">{{ journal.name }}</h3>
            <p class="text-gray-600 text-sm mb-3">{{ journal.description }}</p>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ journal.articles }} articles</span>
              <router-link
                :to="`/article/${journal.slug}`"
                class="text-sm text-green-600 hover:text-green-800 font-medium"
              ><h3 class="text-lg font-semibold text-gray-800 mb-1">View</h3> </router-link>
              
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="bg-white rounded-xl shadow-md overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-2xl font-bold text-gray-800">Featured Paper</h2>
      </div>
      <div class="p-6 text-center text-gray-500">
        Loading papers...
      </div>
    </div>

    <!-- Recent Articles -->
    <div v-if="limitedRecentArticles.length > 0" class="bg-white rounded-xl shadow-md overflow-hidden">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-2xl font-bold text-gray-800">Recent Articles</h2>
      </div>
      <div class="divide-y divide-gray-200">
        <div v-for="article in limitedRecentArticles" :key="article.id" class="p-6 hover:bg-gray-50 transition duration-150">
          <div class="flex flex-col sm:flex-row">
            <div class="flex-1">
                <router-link
                :to="`/article/${article.slug}`"
                class="text-sm text-green-600 hover:text-green-800 font-medium"
              ><h3 class="text-lg font-semibold text-gray-800 mb-1">{{ article.title }}</h3> </router-link>
              <p class="text-gray-600 text-sm mb-2">{{ article.authors.join(', ') }}</p>
              <p class="text-gray-700 mb-3">{{ article.abstract.substring(0, 200) }}...</p>
              <div class="flex flex-wrap gap-2">
                <span v-for="tag in article.tags" :key="tag" class="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">{{ tag }}</span>
              </div>
            </div>
            <div class="sm:w-40 sm:pl-4 mt-4 sm:mt-0 flex sm:flex-col justify-between">
              <span class="text-sm text-gray-500">{{ formatDate(article.publishedDate) }}</span>
              <!-- <button @click="$emit('view-article', article.slug)" class="text-sm text-green-600 hover:text-green-800 font-medium">Read More</button> -->
               <router-link
                :to="`/article/${article.slug}`"
                class="text-sm text-green-600 hover:text-green-800 font-medium"
              >
                Read More
              </router-link>

            </div>
          </div>
        </div>
        <div v-if="articles.length > 10" class="p-4 border-t border-gray-200 text-center">
          <button @click="$emit('update-view', 'browse')" class="px-4 py-2 text-green-600 hover:text-green-800 font-medium">
            View All Articles
          </button>
        </div>
      </div>
    </div>
    <div v-else class="bg-white rounded-xl shadow-md overflow-hidden">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-2xl font-bold text-gray-800">Recent Articles</h2>
      </div>
      <div class="p-6 text-center text-gray-500">
        Loading articles...
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase' 
import { getDocs, collection, query, where } from 'firebase/firestore'

export default {
  name: 'HomeView',
  props: {
    researchers: {
      type: Array,
      default: () => []
    },
    articles: {
      type: Array,
      default: () => []
    },
    featuredJournals: {
      type: Array,
      default: () => []
    },
    recentArticles: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update-view', 'view-article', 'view-journal'],
  computed: {
    limitedRecentArticles() {
      return this.recentArticles.slice(0, 10)
    }
  },
  methods: {
    async countResearchers() {
  try {
    const researcherQuery = query(
      collection(db, 'users'),
      where('role', '==', 'researcher')
    )
    const querySnapshot = await getDocs(researcherQuery)
    this.researchersCount = querySnapshot.size // count only
  } catch (error) {
    console.error('Error fetching researchers:', error)
  }
},
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(dateString).toLocaleDateString(undefined, options)
    }
  },
  async countResearchers() {
  try {
    const researcherQuery = query(
      collection(db, 'users'),
      where('role', '==', 'researcher')
    )
    const querySnapshot = await getDocs(researcherQuery)
    this.researchersCount = querySnapshot.size // count only
  } catch (error) {
    console.error('Error fetching researchers:', error)
  }
},
  mounted() {
      this.countResearchers()
    console.log('HomeView mounted with data:', {
      researchers: this.researchers,
      articles: this.articles,
      featuredJournals: this.featuredJournals,
      recentArticles: this.recentArticles
    })
  }
}
</script>
